import { Type } from "@angular/core";
import { BuildingService } from "../../services/building.service";
import { BpeService } from "../../modules/bpe/bpe.service";
import { ImageService } from "../../services/image.service";
import { EventCreationFormService } from "../../services/event.creation.form.service";
import { GroupService } from "../../services/group.service";
import { MasterDefinitionTagsService } from "../../services/master.definitions/master.definition.tags.service";
import { SearchCriteriaService } from "../../services/search/search-criteria/search.criteria.service";
import { SpaceService } from "../../services/space.service";
import { TaxesService } from "../../services/taxes.service";
import { S25Util } from "../s25-util";
import { CustomAttributes } from "../../pojo/CustomAttributes";
import { ItemStore } from "./item.store";
import { IdItem } from "./item.store.internal";

function createCriteriaStore<T extends string | number>(
    fetchAll: () => Promise<IdItem<T>[]>,
    typeId: T,
): Type<ItemStore<T>>;
function createCriteriaStore<T extends string | number | undefined = undefined>(
    fetchAll: () => Promise<IdItem<T>[]>,
): Type<ItemStore<T | undefined>>;
function createCriteriaStore<T extends string | number | undefined>(
    fetchAll: () => Promise<IdItem<T>[]>,
    typeId?: T,
): Type<ItemStore<T>> | Type<ItemStore<T | undefined>> {
    if (typeId) {
        return ItemStore.create<T>({
            typeId,
            loaders: [{ handles: { all: true }, fetchAll }],
        });
    } else {
        return ItemStore.create<T | undefined>({
            typeId: undefined,
            loaders: [{ handles: { all: true }, fetchAll }],
        });
    }
}

export const SecurityGroupsStore = createCriteriaStore(() => SearchCriteriaService.getSecurityGroups(), 99);
export const SecurityGroupCategoriesStore = createCriteriaStore(() => GroupService.getGroupCategoriesForFilter());
export const AssignedEventStatesStore = createCriteriaStore(() => SearchCriteriaService.getAssignedEventStates());
export const EventStatesStore = createCriteriaStore(() => SearchCriteriaService.getEventStates());
export const EventTypesStore = ItemStore.create({
    typeId: 9,
    loaders: [
        {
            handles: { all: true },
            getParams: (params) =>
                ItemStore.Loader.filterParams(params, ["useSecurity", "activeOnly", "formConfigOnly"]),
            fetchAll: (params) =>
                SearchCriteriaService.getEventTypes(
                    params?.["useSecurity"] ?? false,
                    params?.["activeOnly"] ?? false,
                    params?.["formConfigOnly"] ?? false,
                ),
        },
    ],
});
export const EventCabinetsStore = createCriteriaStore(() => SearchCriteriaService.getEventCabinets());
export const EventSearchesStore = createCriteriaStore(() => SearchCriteriaService.getEventSearches());
export const EventCategoriesStore = ItemStore.create({
    typeId: undefined,
    loaders: [
        {
            handles: { all: true },
            getParams: (params) =>
                ItemStore.Loader.filterParams(params, ["useSecurity", "eventTypeId", "omitReadOnly"]),
            fetchAll: (params) =>
                SearchCriteriaService.getEventCategories(
                    params?.useSecurity,
                    params?.eventTypeId,
                    params?.omitReadOnly,
                ),
        },
    ],
});
export const EventRequirementsStore = ItemStore.create({
    typeId: undefined,
    loaders: [
        {
            handles: { all: true },
            getParams: (params) =>
                ItemStore.Loader.filterParams(params, ["useSecurity", "eventTypeId", "customFilterValue"]),
            fetchAll: (params) =>
                SearchCriteriaService.getEventRequirements(
                    params?.useSecurity,
                    params?.eventTypeId,
                    params?.customFilterValue,
                ),
        },
    ],
});
export const EventRolesStore = ItemStore.create({
    typeId: undefined,
    loaders: [
        {
            handles: { all: true },
            getParams: (params) => ItemStore.Loader.filterParams(params, ["useSecurity", "typedRolesOnly"]),
            fetchAll: (params) => SearchCriteriaService.getEventRoles(params?.useSecurity, params?.typedRolesOnly),
        },
    ],
});
export const EventFoldersStore = ItemStore.create({
    typeId: 1,
    loaders: [
        {
            handles: { all: true },
            getParams: (params) =>
                ItemStore.Loader.filterParams(params, [
                    "cacheId",
                    "pageNum",
                    "pageSize",
                    "filter",
                    "filterFavorites",
                    "domainFilter",
                    "customFilterValue",
                ]),
            fetchAll: async (params) => {
                const data = await SearchCriteriaService.getFolders(
                    params?.cacheId,
                    params?.pageNum,
                    params?.pageSize,
                    params?.filter,
                    params?.filterFavorites,
                    params?.domainFilter,
                    params?.customFilterValue,
                );
                return SearchCriteriaService.extractItems(data);
            },
        },
    ],
});
export const EventCustomAttributesStore = ItemStore.create({
    typeId: undefined,
    loaders: [
        {
            handles: { all: true },
            getParams: (params) => ItemStore.Loader.filterParams(params, ["useSecurity"]),
            fetchAll: (params) => SearchCriteriaService.getEventCustomAttributes(params?.useSecurity as boolean),
        },
    ],
});
export const EventTypeReportsStore = createCriteriaStore(() => SearchCriteriaService.getEventTypeReports(), 7);
export const LocationRelatedTypesStore = createCriteriaStore(() => SearchCriteriaService.getLocationRelatedTypes());
export const LocationSearchesStore = createCriteriaStore(() => SearchCriteriaService.getLocationSearches());
export const LocationFeaturesStore = ItemStore.create({
    typeId: undefined,
    loaders: [
        {
            handles: { all: true },
            getParams: (params) => ItemStore.Loader.filterParams(params, ["customFilterValue", "omitReadOnly"]),
            fetchAll: (params) =>
                // first two params are unused; only defined for JS param order
                SpaceService.getSpaceFeatures(undefined, undefined, params?.customFilterValue, params?.omitReadOnly),
        },
    ],
});
export const LocationCategoriesStore = ItemStore.create({
    typeId: undefined,
    loaders: [
        {
            handles: { all: true },
            getParams: (params) => ItemStore.Loader.filterParams(params, ["omitReadOnly"]),
            fetchAll: (params) => SearchCriteriaService.getSpaceCategories(params?.omitReadOnly),
        },
    ],
});
export const LocationPartitionsStore = createCriteriaStore(() => SpaceService.getLocationPartitions());
export const LocationLayoutsStore = ItemStore.create({
    typeId: undefined,
    loaders: [
        {
            handles: { all: true },
            getParams: (params) => ItemStore.Loader.filterParams(params, ["customFilterValue"]),
            fetchAll: (params) =>
                // first two params are unused; only defined for JS param order
                SearchCriteriaService.getSpaceLayouts(undefined, undefined, params?.customFilterValue),
        },
    ],
});
export const LocationCustomAttributesStore = createCriteriaStore<CustomAttributes.Type>(() =>
    SearchCriteriaService.getLocationCustomAttributes(),
);
export const OrganizationRolesStore = ItemStore.create({
    typeId: undefined,
    loaders: [
        {
            handles: { all: true },
            getParams: (params) => ItemStore.Loader.filterParams(params, ["useSecurity", "activeOnly"]),
            fetchAll: (params) => SearchCriteriaService.getOrganizationRoles(params?.useSecurity, params?.activeOnly),
        },
    ],
});
export const OrganizationSearchesStore = createCriteriaStore(() => SearchCriteriaService.getOrganizationSearches());
export const OrganizationTypesStore = createCriteriaStore(() => SearchCriteriaService.getOrganizationTypes());
export const OrganizationCategoriesStore = createCriteriaStore(() => SearchCriteriaService.getOrganizationCategories());
export const OrganizationRatingsStore = createCriteriaStore(() => SearchCriteriaService.getOrganizationRatings());
export const OrganizationCustomAttributesStore = createCriteriaStore(() =>
    SearchCriteriaService.getOrganizationCustomAttributes(),
);
export const ResourceSearchesStore = createCriteriaStore(() => SearchCriteriaService.getResourceSearches());
export const ResourceCategoriesStore = createCriteriaStore(() => SearchCriteriaService.getResourceCategories());
export const ResourceCustomAttributesStore = createCriteriaStore(() =>
    SearchCriteriaService.getResourceCustomAttributes(),
);
export const ResourcesQuantityStore = ItemStore.create({
    typeId: 607 as number,
    loaders: [
        {
            handles: { all: true },
            getParams: (params) =>
                ItemStore.Loader.filterParams(params, [
                    "cacheId",
                    "pageNum",
                    "pageSize",
                    "filter",
                    "filterFavorites",
                    "domainFilter",
                    "customFilterValue",
                ]),
            fetchAll: async (params) => {
                const data = await SearchCriteriaService.getResourcesQuantity(
                    params?.cacheId,
                    params?.pageNum,
                    params?.pageSize,
                    params?.filter,
                    params?.filterFavorites,
                    params?.domainFilter,
                    params?.customFilterValue,
                );
                return SearchCriteriaService.extractItems(data)
                    .map((item: any) => ({ ...item, itemTypeId: 607, quantityMax: item.quantity, quantity: undefined }))
                    .filter((item: any) => item.quantityMax > 0);
            },
        },
    ],
});
export const ContactCustomAttributesStore = createCriteriaStore(() =>
    SearchCriteriaService.getContactCustomAttributes(),
);
export const EmailTemplatesStore = createCriteriaStore(
    () => BpeService.getFullTemplates() as Promise<IdItem<undefined>[]>,
);
export const ManualEmailTemplatesStore = ItemStore.create({
    typeId: undefined,
    loaders: [
        {
            handles: { all: true },
            getParams: (params) => ItemStore.Loader.filterParams(params, ["useSecurity", "omitReadOnly"]),
            fetchAll: (params) =>
                SearchCriteriaService.getManualTemplates(params?.useSecurity, params?.omitReadOnly) as Promise<
                    IdItem<undefined>[]
                >,
        },
    ],
});
export const SystemTagsStore = createCriteriaStore(async () => {
    const tags = await MasterDefinitionTagsService.getTags();
    return tags.map((tag) => ({ itemId: tag.tag_id, itemName: tag.tag_name }));
});
export const TodoTemplatesStore = createCriteriaStore(
    () => BpeService.getFullTodoTemplates() as Promise<IdItem<undefined>[]>,
);
export const TaxSchedulesStore = createCriteriaStore(async () => {
    const taxes = await TaxesService.getTaxes();
    return taxes?.map((tax: any) => ({ itemId: tax.tax_id, itemName: tax.tax_name })) ?? [];
});
export const StandardSchedulesStore = createCriteriaStore(() => SearchCriteriaService.getStandardSchedules());
export const MatchQuestionsStore = ItemStore.create({
    typeId: undefined,
    loaders: [
        {
            handles: { all: true },
            getParams: (params) => ItemStore.Loader.filterParams(params, ["customFilterValue"]),
            fetchAll: (params) => SearchCriteriaService.getQuestionItems(S25Util.parseInt(params?.customFilterValue)),
        },
    ],
});
export const MatchQuestionsAllStore = ItemStore.create({
    typeId: undefined,
    loaders: [
        {
            handles: { all: true },
            getParams: (params) => ItemStore.Loader.filterParams(params, ["customFilterValue"]),
            fetchAll: (params) =>
                SearchCriteriaService.getAllQuestionItems(S25Util.parseInt(params?.customFilterValue)),
        },
    ],
});
export const NonTextMatchQuestionsStore = ItemStore.create({
    typeId: undefined,
    loaders: [
        {
            handles: { all: true },
            getParams: (params) => ItemStore.Loader.filterParams(params, ["customFilterValue"]),
            fetchAll: (params) =>
                SearchCriteriaService.getNonTextQuestionItems(S25Util.parseInt(params?.customFilterValue)),
        },
    ],
});
export const MatchGroupsStore = ItemStore.create({
    typeId: undefined,
    loaders: [
        {
            handles: { all: true },
            getParams: (params) => ItemStore.Loader.filterParams(params, ["customFilterValue"]),
            fetchAll: (params) => SearchCriteriaService.getMatchGroups(S25Util.parseInt(params?.customFilterValue)),
        },
    ],
});
export const IngestionPointsStore = createCriteriaStore(() => SearchCriteriaService.getAllIngestionPoints());
export const IngestionFieldsAllStore = ItemStore.create({
    typeId: undefined,
    loaders: [
        {
            handles: { all: true },
            getParams: (params) => ItemStore.Loader.filterParams(params, ["customFilterValue"]),
            fetchAll: (params) =>
                SearchCriteriaService.getAllIngestionFieldItems(S25Util.parseInt(params?.customFilterValue)),
        },
    ],
});
export const WorkflowProcessesStore = createCriteriaStore(() => SearchCriteriaService.getAllWorkflowProcessItems());
export const FormConfigsStore = createCriteriaStore(() => EventCreationFormService.getAllConfigs());
export const BudgetsStore = ItemStore.create({
    typeId: undefined,
    loaders: [
        {
            handles: { all: true },
            getParams: (params) => ItemStore.Loader.filterParams(params, ["organizationId"]),
            fetchAll: (params) =>
                // first 3 params ignored
                SearchCriteriaService.getBudgets(
                    undefined,
                    undefined,
                    undefined,
                    params?.organizationId as number,
                ) as Promise<IdItem<undefined>[]>,
        },
    ],
});
export const BudgetPlanTypesStore = createCriteriaStore(() => SearchCriteriaService.getBudgetPlanTypes());
export const FiscalYearsStore = createCriteriaStore(() => SearchCriteriaService.getFiscalYears());
export const ElectionPositionsStore = createCriteriaStore(() => SearchCriteriaService.getElectionPositionSearchList());
export const ElectionPositionsForElectionStore = ItemStore.create({
    typeId: undefined,
    loaders: [
        {
            handles: { all: true },
            getParams: (params) => ItemStore.Loader.filterParams(params, ["customFilterValue"]),
            fetchAll: (params) =>
                SearchCriteriaService.getElectionPositionSearchListForElection(params?.customFilterValue),
        },
    ],
});
export const ElectionsStore = createCriteriaStore(() => SearchCriteriaService.getElectionsSearchList());
export const BuildingsStore = createCriteriaStore(async () => {
    const buildings = await BuildingService.getBuildings();
    return buildings.map((b: any) => ({ itemId: b.id, itemName: b.buildingName })) ?? [];
});
export const ImagesStore = createCriteriaStore(async () => {
    const images = await ImageService.getImages();
    return (
        images?.map((image) => ({
            itemId: image.image_id!,
            itemName: image.image_name,
            itemDesc: image.image_desc,
        })) ?? []
    );
});
